import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, CirclePlus, Clock, DollarSign, RefreshCw } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { damageNoteService } from '../../services/damageNoteService';
import { DamageNoteListDTO, DamageNoteStatus, DamageNoteStatsDTO } from '../../types';
import './DamageNotePage.css';

// ─── Component ────────────────────────────────────────────────────────────────

const DamageNoteListPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t }     = useTranslation();

  const STATUS_LABEL: Record<DamageNoteStatus, string> = {
    Draft:           t('statuses.draft'),
    PendingApproval: t('statuses.pendingApproval'),
    Approved:        t('statuses.approved'),
    Rejected:        t('statuses.rejected'),
    Posted:          t('statuses.posted'),
    Cancelled:       t('statuses.cancelled'),
  };

  const STATUS_CLASS: Record<DamageNoteStatus, string> = {
    Draft:           'ims-badge dn-badge-draft',
    PendingApproval: 'ims-badge dn-badge-pending',
    Approved:        'ims-badge dn-badge-approved',
    Rejected:        'ims-badge dn-badge-rejected',
    Posted:          'ims-badge dn-badge-posted',
    Cancelled:       'ims-badge dn-badge-cancelled',
  };

  const [damageNotes, setDamageNotes] = useState<DamageNoteListDTO[]>([]);
  const [stats, setStats]             = useState<DamageNoteStatsDTO | null>(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [successMsg, setSuccessMsg]   = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!successMsg) return;
    const timer = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(timer);
  }, [successMsg]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, statsRes] = await Promise.all([
        damageNoteService.getAll(1, 100),
        damageNoteService.getStats(),
      ]);
      const payload = listRes.data?.data;
      setDamageNotes(payload?.data ?? []);
      if (statsRes.data?.data) setStats(statsRes.data.data);
    } catch {
      setError(t('damageNote.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'damageNoteNumber',
      label: t('damageNote.damageNoteNumber'),
      sortable: true,
      width: '160px',
    },
    {
      key: 'damageDate',
      label: t('damageNote.damageDate'),
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
    { key: 'warehouseName',   label: t('damageNote.warehouse'),     width: '160px' },
    {
      key: 'status',
      label: t('common.status'),
      width: '150px',
      render: (v) => {
        const s = v as DamageNoteStatus;
        return <span className={STATUS_CLASS[s] || 'ims-badge'}>{STATUS_LABEL[s] ?? s}</span>;
      },
    },
    { key: 'itemCount',           label: t('damageNote.items'),          width: '80px' },
    {
      key: 'totalEstimatedValue',
      label: t('damageNote.estimatedValue'),
      width: '140px',
      render: (v) => (v as number).toFixed(2),
    },
    { key: 'reportedByName', label: t('damageNote.reportedBy'),     width: '140px' },
    {
      key: 'createdAt',
      label: t('common.createdAt'),
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
  ];

  const actions: GridAction[] = [
    {
      label: t('common.view'),
      onClick: (row) => navigate(`/damage-notes/${row.id}`),
      className: 'grid-action-btn-view',
    },
  ];

  return (
    <Layout>
      <div className="ims-page dn-page">
        {/* Header */}
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('damageNote.title')}</h1>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={loadData} disabled={loading}>
              <RefreshCw size={14} strokeWidth={2} className={loading ? 'refresh-icon-spinning' : ''} />
              {loading ? t('common.refreshing', 'Refreshing…') : t('common.refresh')}
            </button>
            <button className="btn-new" onClick={() => navigate('/damage-notes/create')}>
              <CirclePlus /> {t('damageNote.newDamageNote')}
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="ims-stats-row">
            <div className="ims-stat-card">
              <div className="ims-stat-icon dn-stat-icon-total"><AlertTriangle size={22} strokeWidth={1.75} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.totalDamageNotes}</span>
                <span className="ims-stat-label">{t('damageNote.totalDamageNotes')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon dn-stat-icon-pending"><Clock size={22} strokeWidth={1.75} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.pendingApproval}</span>
                <span className="ims-stat-label">{t('damageNote.pendingApproval')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon dn-stat-icon-posted"><CheckCircle2 size={22} strokeWidth={1.75} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.posted}</span>
                <span className="ims-stat-label">{t('damageNote.posted')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon dn-stat-icon-loss"><DollarSign size={22} strokeWidth={1.75} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{stats.totalEstimatedLoss.toFixed(2)}</span>
                <span className="ims-stat-label">{t('damageNote.totalEstimatedLoss')}</span>
              </div>
            </div>
          </div>
        )}

        {successMsg && <div className="ims-alert ims-alert-success">{successMsg}</div>}
        {error      && <div className="ims-alert ims-alert-error">{error}</div>}

        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={damageNotes}
            loading={loading}
            actions={actions}
            emptyMessage={t('damageNote.noDamageNotes')}
          />
        </div>
      </div>
    </Layout>
  );
};

export default DamageNoteListPage;
